/**
 * V70 Phase 3-C — 엔글 타자왕 서버 serverApi
 *
 * 기존 Mock (../serverApi.ts) 과 동일한 AcidRainServerApi interface 구현.
 * 세션 발급·점수 제출을 BE Callable 기준으로 처리.
 *
 * Phase 3-C.2 — 재시도 안정성:
 *   - sendChunkWithRetry: chunk 전송 네트워크 오류 시 지수 backoff 재시도 (최대 3회)
 *   - submitWithRetry: 점수 제출도 동일 재시도 (BE 멱등 활용)
 *   - 서버 응답 reused / nextExpectedSeq 검증 — 상태 불일치 감지
 *   - cancel token: 페이지 이동·언마운트 시 재시도 timer 중단
 *   - 영구 오류 (invalid-argument / failed-precondition / unauthenticated) 즉시 throw
 */
import {
  callConsumeAcidRainSession,
  callSubmitAcidRainScore,
  callAppendAcidRainInputEvents,
  callGetPublicTournamentLeaderboard,
  callGetTournamentDailyStatus,
  type PublicLeaderboardRow,
} from '../../shared/tournamentPlayCallables';
import {
  callGetPublicTournamentGameSettings,
  callUpdateTournamentOpenPeriod,
  callUpdateTournamentCoinPolicy,
  callResetTournamentLeaderboard,
  type PublicGameSettingsResp,
} from '../../shared/tournamentGameApi';
import type {
  AcidRainSettings, CoinPolicy, CoinPolicyChange, DailyCoinStatus,
  LeaderboardRow,
} from '../types';
import { DEFAULT_COIN_POLICY, DEFAULT_SETTINGS } from '../types';
import type {
  AcidRainServerApi, LeaderboardMeta,
  ResetLeaderboardRequest, ResetLeaderboardResponse,
  StartSessionRequest, StartSessionResponse,
  SubmitScoreRequest, SubmitScoreResponse,
  UpdateCoinPolicyRequest, UpdateCoinPolicyResponse,
} from '../serverApi';

// Phase 3-C.1 결함 D 수정 — 로컬 counter 전면 제거. 서버 getTournamentDailyStatus 가 공식 기준.
// (이전 S9 설계: localStorage counter — 사용자 지시 §D "로컬 카운터를 공식 잔여 횟수 근거로 사용 금지")

// ─────────── Phase 3-C.2 — 재시도 로직 ───────────

/**
 * 지수 backoff 간격 (ms) — 재시도 N 회차에 대기할 시간.
 * 재시도 상한 = MAX_RETRIES (3). 총 호출 = 4회 (1차 + 3재시도).
 */
export const RETRY_BACKOFF_MS = [500, 1000, 2000];
export const MAX_RETRIES = RETRY_BACKOFF_MS.length;

/**
 * cancel token — 페이지 이동/언마운트 시 재시도 중단에 사용.
 * `cancelled: true` 세팅 시 다음 attempt 전에 Error throw.
 */
export interface CancelToken {
  cancelled: boolean;
}

export function createCancelToken(): CancelToken {
  return { cancelled: false };
}

/**
 * 재시도 분류 — 영구 오류는 즉시 throw, 일시 오류만 backoff 재시도.
 * HttpsError.code 기준 — Firebase SDK 가 'functions/invalid-argument' 형태 또는 code 노출.
 *
 * Phase 3-C.2 보완: testExports 복제 제거 — 테스트가 이 함수를 직접 import.
 */
export function isPermanentError(e: unknown): boolean {
  const code = String((e as any)?.code || '');
  // Firebase SDK 는 'functions/invalid-argument' 또는 'invalid-argument' 양쪽 노출 가능
  const permanentCodes = [
    'invalid-argument', 'failed-precondition', 'unauthenticated',
    'permission-denied', 'not-found', 'out-of-range',
  ];
  for (const pc of permanentCodes) {
    if (code === pc || code.endsWith('/' + pc)) return true;
  }
  // 메시지 기반 보조 (BE 가 명시한 패턴)
  const msg = String((e as any)?.message || '');
  if (/seq|겹침|뛰어넘음|비연속|중복|내용 불일치|unauthenticated|로그인/.test(msg)) {
    // seq/중복 관련: BE 멱등 로직이 처리 못한 상태 — 재시도 무의미
    return true;
  }
  return false;
}

export async function sleep(ms: number, token?: CancelToken): Promise<void> {
  return new Promise((resolve, reject) => {
    const checkInterval = 50;
    let elapsed = 0;
    const timer = setInterval(() => {
      elapsed += checkInterval;
      if (token?.cancelled) {
        clearInterval(timer);
        reject(new Error('재시도 중단 (cancel token)'));
      } else if (elapsed >= ms) {
        clearInterval(timer);
        resolve();
      }
    }, checkInterval);
  });
}

/**
 * 범용 재시도 wrapper — 일시 오류에만 지수 backoff 재시도.
 * - 성공: 결과 반환
 * - 영구 오류: 즉시 throw
 * - MAX_RETRIES 소진: 마지막 오류 throw
 * - cancel token: sleep 중 cancelled 체크 + 각 attempt 전 cancelled 체크
 *
 * Phase 3-C.2 보완: export — 테스트가 직접 호출 (testExports 복제 제거).
 */
export async function callWithRetry<T>(
  fn: () => Promise<T>,
  label: string,
  token?: CancelToken,
): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    if (token?.cancelled) throw new Error(`${label} 중단 (cancel token)`);
    try {
      return await fn();
    } catch (e) {
      lastErr = e;
      if (isPermanentError(e)) throw e;
      if (attempt === MAX_RETRIES) break;
      await sleep(RETRY_BACKOFF_MS[attempt], token);
    }
  }
  throw new Error(`${label} 재시도 소진 (${MAX_RETRIES + 1}회 시도): ${(lastErr as any)?.message || String(lastErr)}`);
}

/**
 * chunk 전송 + 서버 응답 검증.
 * - 응답 reused=true: 이미 저장된 chunk — nextExpectedSeq 불변 확인
 * - 응답 reused=false: 신규 저장 — nextExpectedSeq 가 기대값 (startSeq + chunk.length) 과 일치해야
 * - 서버 상태 불일치 시 Error throw (복구 불가)
 */
async function sendChunkWithValidation(
  input: Parameters<typeof callAppendAcidRainInputEvents>[0],
  expectedStartSeq: number,
  chunkLength: number,
  token?: CancelToken,
): Promise<void> {
  const resp = await callWithRetry(
    () => callAppendAcidRainInputEvents(input),
    `chunk-${expectedStartSeq}`,
    token,
  );
  // 사용자 지시 §2 "서버가 반환한 reused 및 nextExpectedSeq 검증"
  if (resp.reused === true) {
    // 이미 저장된 상태 — nextExpectedSeq 는 변경되지 않거나 더 큼
    if (resp.nextExpectedSeq < expectedStartSeq + chunkLength) {
      throw new Error(
        `chunk-${expectedStartSeq} reused=true but nextExpectedSeq=${resp.nextExpectedSeq} < 기대=${expectedStartSeq + chunkLength}`,
      );
    }
    return;
  }
  // 신규 처리 — nextExpectedSeq 가 정확히 startSeq + length 여야
  if (resp.nextExpectedSeq !== expectedStartSeq + chunkLength) {
    throw new Error(
      `chunk-${expectedStartSeq} 응답 nextExpectedSeq=${resp.nextExpectedSeq} 기대=${expectedStartSeq + chunkLength}`,
    );
  }
}

// ─────────── 매핑 유틸 ───────────

function toCoinPolicy(pub: PublicGameSettingsResp): CoinPolicy {
  if (pub.coinPolicy && (pub.coinPolicy.mode === 'limited' || pub.coinPolicy.mode === 'unlimited')) {
    const n = Math.max(1, Math.min(100, Math.floor(Number(pub.coinPolicy.dailyLimit) || DEFAULT_COIN_POLICY.dailyLimit)));
    return { mode: pub.coinPolicy.mode, dailyLimit: n };
  }
  return { ...DEFAULT_COIN_POLICY };
}

function toAcidRainSettings(pub: PublicGameSettingsResp): AcidRainSettings {
  const policy = toCoinPolicy(pub);
  return {
    ...DEFAULT_SETTINGS,
    enabled: pub.enabled,
    dailyCoinLimit: policy.dailyLimit,
    openStartAt: pub.openStartAt ? Date.parse(pub.openStartAt) : null,
    openEndAt: pub.openEndAt ? Date.parse(pub.openEndAt) : null,
  };
}

function uidFromCurrent(): string | null {
  try {
    // Firebase Auth 접근은 호출 지점에서 전달받는 user 가 신뢰 소스.
    // 로컬 counter 는 uid 매개변수에 의존하므로 별도 접근 불필요.
    return null;
  } catch { return null; }
}
void uidFromCurrent;  // 추후 사용 — 지금은 호출 측에서 uid 전달

// ─────────── 어드민 Phase 3-A 변경 이력 캐시 (서버 전환 모드에서는 축소) ───────────
// 변경 이력 UI 는 현재 admin page 전용 — serverApi 는 빈 배열 반환.

export const serverAcidRainApi: AcidRainServerApi = {
  async getSettings(): Promise<AcidRainSettings> {
    const pub = await callGetPublicTournamentGameSettings('acidRain');
    return toAcidRainSettings(pub);
  },

  async getDailyCoinStatus(): Promise<DailyCoinStatus> {
    // Phase 3-C.1 결함 D 수정 — 서버 getTournamentDailyStatus 로 교체.
    // 로컬 counter 완전 제거. localStorage 삭제·브라우저 변경에도 서버 기준 유지.
    const r = await callGetTournamentDailyStatus({ gameKey: 'acidRain' });
    return {
      ymd: r.ymd,
      mode: r.mode,
      used: r.used,
      limit: r.limit,
      remaining: r.remaining,
    };
  },

  async startSession(req: StartSessionRequest): Promise<StartSessionResponse> {
    const resp = await callConsumeAcidRainSession({ clientRequestId: req.clientRequestId });
    // Phase 3-C.1 — 로컬 counter 완전 제거 (서버 getTournamentDailyStatus 가 공식 기준)
    return {
      sessionId: resp.sessionId,
      seed: resp.seed,
      commitmentHash: '',  // 서버는 commitmentHash 미사용 — 호환 shape 유지용
      settings: {
        ...DEFAULT_SETTINGS,
        enabled: true,
        initialLives: resp.settings.initialLives,
        durationSec: resp.settings.durationSec,
        difficultyRampSec: resp.settings.difficultyRampSec,
        fallSpeedMultiplier: resp.settings.fallSpeedMultiplier,
        spawnIntervalDivisor: resp.settings.spawnIntervalDivisor,
      },
      startedAtServerMs: resp.startedAtServerMs,
      leaderboardEpoch: resp.leaderboardEpoch,
    };
  },

  async submitScore(req: SubmitScoreRequest): Promise<SubmitScoreResponse> {
    // Phase 3-C.2 — chunk flush + submit 모두 지수 backoff 재시도. BE 멱등 활용.
    //   - chunk: 완전 동일 재전송은 reused=true, 다른 reqId 또는 다른 내용은 거부
    //   - submit: 세션 completed 재호출은 reused=true (저장 결과 반환)
    //   - cancel token: 호출자가 전달 (언마운트 시 cancelled=true → backoff sleep 즉시 중단)
    const events = req.inputEvents || [];
    const CHUNK_SIZE = 200;  // BE acidRain.ts appendAcidRainInputEvents 상한
    const token: CancelToken | undefined = req.cancelToken as CancelToken | undefined;
    for (let i = 0; i < events.length; i += CHUNK_SIZE) {
      const chunk = events.slice(i, i + CHUNK_SIZE);
      // chunk clientRequestId 는 **고정** — 재전송 시 BE 가 같은 reqId + 같은 내용 → reused.
      // startSeq 를 reqId suffix 로 사용해 chunk 별 고유성 확보 (BE 멱등 비교는 reqId + 내용).
      const chunkReqId = `${req.clientRequestId}-chunk-${chunk[0].seq}`;
      await sendChunkWithValidation(
        { sessionId: req.sessionId, clientRequestId: chunkReqId, events: chunk },
        chunk[0].seq,
        chunk.length,
        token,
      );
    }
    // 모든 chunk flush 완료 후 submit — 재시도 포함
    const resp = await callWithRetry(
      () => callSubmitAcidRainScore({
        sessionId: req.sessionId,
        clientRequestId: req.clientRequestId,
        result: {
          clientReportedScore: req.result.clientReportedScore,
          cleared: req.result.cleared,
          missed: req.result.missed,
          maxCombo: req.result.maxCombo,
          reason: req.result.reason,
        },
      }),
      'submitScore',
      token,
    );
    return {
      accepted: resp.flagged === 'none',  // flagged 상태면 공식 기록 아님
      serverVerifiedScore: resp.serverScore,
      rank: resp.rank ?? undefined,
      isPersonalBest: resp.isPersonalBest,
      isMock: false,
      flagged: resp.flagged,  // UI 가 안내 분기 (사용자 지시 §A)
    };
  },

  async getLeaderboard(): Promise<LeaderboardRow[]> {
    const lb = await callGetPublicTournamentLeaderboard({ gameKey: 'acidRain', limit: 10 });
    return lb.rows.map(toLeaderboardRow);
  },

  async getMyBestScore(uid: string): Promise<{ bestScore: number | null; rank: number | null; epoch: number }> {
    const lb = await callGetPublicTournamentLeaderboard({ gameKey: 'acidRain', limit: 10 });
    if (lb.self && lb.self.uid === uid) {
      return { bestScore: lb.self.bestScore, rank: lb.self.rank, epoch: lb.epoch };
    }
    return { bestScore: null, rank: null, epoch: lb.epoch };
  },

  async getLeaderboardMeta(): Promise<LeaderboardMeta> {
    const pub = await callGetPublicTournamentGameSettings('acidRain');
    return {
      epoch: Number.isFinite(pub.leaderboardEpoch) && pub.leaderboardEpoch > 0 ? pub.leaderboardEpoch : 1,
      lastResetAt: pub.lastResetAt ? Date.parse(pub.lastResetAt) : null,
      lastResetByName: null,
      lastResetReason: pub.lastResetReason,
    };
  },

  async resetLeaderboard(req: ResetLeaderboardRequest): Promise<ResetLeaderboardResponse> {
    const newEpoch = await callResetTournamentLeaderboard('acidRain', req.reason);
    return {
      success: true,
      epoch: newEpoch,
      resetAt: Date.now(),
      resetByName: null,
      reason: req.reason,
      isMock: false,
    };
  },

  async getCoinPolicy(): Promise<CoinPolicy> {
    const pub = await callGetPublicTournamentGameSettings('acidRain');
    return toCoinPolicy(pub);
  },

  async updateCoinPolicy(req: UpdateCoinPolicyRequest): Promise<UpdateCoinPolicyResponse> {
    const prev = await serverAcidRainApi.getCoinPolicy();
    await callUpdateTournamentCoinPolicy({ mode: req.policy.mode, dailyLimit: req.policy.dailyLimit });
    return {
      success: true,
      previous: prev,
      next: req.policy,
      changedAt: Date.now(),
      isMock: false,
    };
  },

  async getCoinPolicyHistory(_opts): Promise<CoinPolicyChange[]> {
    // 서버 모드에서 변경 이력은 Phase 3-A admin callable (getTournamentGameSettingsHistory) 로 조회.
    // 현재 FE 호출 지점은 admin 페이지 전용 — serverApi 는 빈 배열 반환.
    return [];
  },

  async updateOpenPeriod(req): Promise<{ success: boolean; openStartAt: number | null; openEndAt: number | null; isMock: boolean }> {
    const start = req.openStartAt;
    const end = req.openEndAt;
    if (start != null && end != null && start >= end) {
      throw new Error('종료 일시는 시작 일시보다 뒤여야 합니다.');
    }
    await callUpdateTournamentOpenPeriod(
      'acidRain',
      start != null ? new Date(start).toISOString() : null,
      end != null ? new Date(end).toISOString() : null,
    );
    return { success: true, openStartAt: start, openEndAt: end, isMock: false };
  },
};

function toLeaderboardRow(r: PublicLeaderboardRow): LeaderboardRow {
  return {
    rank: r.rank,
    uid: r.uid,
    displayName: r.displayName,
    companyCode: r.companyCode ?? undefined,
    bestScore: r.bestScore,
    recordedAt: 0,  // BE 는 recordedAt 미반환 — UI 에서는 사용 안 하거나 상대 시간 미표시
    orgUnitId: r.orgUnitId ?? undefined,
  };
}
